import React, { useState, useEffect } from 'react';
import { 
  Form, 
  Input, 
  Select, 
  DatePicker, 
  Button, 
  Space, 
  Row, 
  Col, 
  InputNumber,
  message 
} from 'antd';
import apiService from "services/api/apiService";
import { Employee } from '../../types/employee';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;

interface EmployeeFormProps {
  employee?: Employee | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const EmployeeForm: React.FC<EmployeeFormProps> = ({ employee, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (employee) {
      form.setFieldsValue({
        ...employee,
        hire_date: employee.hire_date ? dayjs(employee.hire_date) : null,
        date_of_birth: employee.date_of_birth ? dayjs(employee.date_of_birth) : null,
        basic_salary: employee.basic_salary ? parseFloat(employee.basic_salary) : 0,
        allowances: employee.allowances ? parseFloat(employee.allowances) : 0,
      });
    }
  }, [employee, form]);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      
      // Prepare complete employee data with all required fields
      const employeeData = {
        // Required fields
        employee_id: values.employee_id,
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        department: values.department,
        position: values.position,
        hire_date: values.hire_date ? values.hire_date.format('YYYY-MM-DD') : null,
        
        // Optional fields with defaults
        phone: values.phone || '',
        date_of_birth: values.date_of_birth ? values.date_of_birth.format('YYYY-MM-DD') : null,
        gender: values.gender || null,
        marital_status: values.marital_status || null,
        nationality: values.nationality || null,
        address: values.address || null,
        emergency_contact_name: values.emergency_contact_name || null,
        emergency_contact_phone: values.emergency_contact_phone || null,
        role: values.role || 'staff',
        employment_type: values.employment_type || 'full_time',
        work_location: values.work_location || null,
        reporting_manager: values.reporting_manager || null,
        basic_salary: values.basic_salary ? values.basic_salary.toString() : '0.00',
        allowances: values.allowances ? values.allowances.toString() : '0.00',
        bank_name: values.bank_name || null,
        account_number: values.account_number || null,
        account_name: values.account_name || null,
        status: values.status || 'active',
        notes: values.notes || null
      };

      let response;
      if (employee) {
        response = await apiService.updateEmployee(employee.id.toString(), employeeData);
      } else {
        response = await apiService.createEmployee(employeeData);
      }

      if (response.success) {
        message.success(employee ? 'Employee updated successfully' : 'Employee created successfully');
        form.resetFields();
        onSuccess();
      } else {
        message.error(response.error || 'Operation failed');
      }
    } catch (error: any) {
      console.error('Employee operation error:', error);
      message.error(error.response?.data?.error || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        status: 'active',
        employment_type: 'full_time',
        role: 'staff',
        basic_salary: 0,
        allowances: 0
      }}
    >
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="first_name"
            label="First Name"
            rules={[{ required: true, message: 'Please enter first name' }]}
          >
            <Input placeholder="Enter first name" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="last_name"
            label="Last Name"
            rules={[{ required: true, message: 'Please enter last name' }]}
          >
            <Input placeholder="Enter last name" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="employee_id"
            label="Employee ID"
            rules={[{ required: true, message: 'Please enter employee ID' }]}
          >
            <Input placeholder="Enter employee ID" disabled={!!employee} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter valid email' }
            ]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="phone" label="Phone Number">
            <Input placeholder="Enter phone number" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="date_of_birth" label="Date of Birth">
            <DatePicker style={{ width: '100%' }} placeholder="Select date of birth" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item name="gender" label="Gender">
            <Select placeholder="Select gender" allowClear>
              <Option value="male">Male</Option>
              <Option value="female">Female</Option>
              <Option value="other">Other</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="marital_status" label="Marital Status">
            <Select placeholder="Select marital status" allowClear>
              <Option value="single">Single</Option>
              <Option value="married">Married</Option>
              <Option value="divorced">Divorced</Option>
              <Option value="widowed">Widowed</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="nationality" label="Nationality">
            <Input placeholder="Enter nationality" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="address" label="Address">
        <TextArea rows={3} placeholder="Enter address" />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="emergency_contact_name" label="Emergency Contact Name">
            <Input placeholder="Enter emergency contact name" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="emergency_contact_phone" label="Emergency Contact Phone">
            <Input placeholder="Enter emergency contact phone" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            name="department"
            label="Department"
            rules={[{ required: true, message: 'Please enter department' }]}
          >
            <Input placeholder="Enter department" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="position"
            label="Position"
            rules={[{ required: true, message: 'Please enter position' }]}
          >
            <Input placeholder="Enter position" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="hire_date"
            label="Hire Date"
            rules={[{ required: true, message: 'Please select hire date' }]}
          >
            <DatePicker style={{ width: '100%' }} placeholder="Select hire date" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item name="employment_type" label="Employment Type">
            <Select>
              <Option value="full_time">Full Time</Option>
              <Option value="part_time">Part Time</Option>
              <Option value="contract">Contract</Option>
              <Option value="intern">Intern</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="role" label="Role">
            <Select>
              <Option value="staff">Staff</Option>
              <Option value="hr_manager">HR Manager</Option>
              <Option value="admin">Admin</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="status" label="Status">
            <Select>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="basic_salary" label="Basic Salary (₦)">
            <InputNumber 
              style={{ width: '100%' }} 
              placeholder="Enter basic salary"
              min={0}
              step={1000}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="allowances" label="Allowances (₦)">
            <InputNumber 
              style={{ width: '100%' }} 
              placeholder="Enter allowances"
              min={0}
              step={1000}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item name="bank_name" label="Bank Name">
            <Input placeholder="Enter bank name" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="account_number" label="Account Number">
            <Input placeholder="Enter account number" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="account_name" label="Account Name">
            <Input placeholder="Enter account name" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="notes" label="Notes">
        <TextArea rows={3} placeholder="Enter additional notes" />
      </Form.Item>

      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={loading}>
            {employee ? 'Update Employee' : 'Create Employee'}
          </Button>
          <Button onClick={onCancel}>
            Cancel
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default EmployeeForm;
